/**
 * Um "Próximo passo" por caso nas telas da equipe (E162.F2.T01, ADR-0111 §2.6; QA-01/11/19/21/28).
 *
 * Na bateria de 21/10 cada tela lia o caso por uma régua própria: "5 de 5 verificados" com o laudo
 * recusado, "Consumível" com o "Conferir agora" recusado. A faixa lê a prontidão do servidor — a
 * mesma função dos portões do laudo, do preliminar, da conferência da saída e da assinatura — e diz
 * **uma** ação, o motivo e onde ela se faz. As demais pendências ficam por expansão, na ordem.
 *
 * Fora da raia eletrônica (grafotécnica) e sem permissão a faixa não aparece; o retrato que não foi
 * lido agora diz isso, sem inventar passo.
 */
import type { AxiosInstance } from 'axios';
import { Link, useLocation } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import { caminhoDaTela, porqueDaPendencia as porque, telaDoPasso } from '@/lib/proximoPasso';
import {
  bloqueia,
  type PendenciaDaProntidaoData,
  type ProntidaoDoCasoData,
} from '@/lib/resultadoAuditado';
import { useResultadoDoCaso, type EstadoDoResultadoNaTela } from '@/lib/useResultadoDoCaso';

const t = ptBR.resultadoAuditado;

export interface ProximoPassoProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando a tela que hospeda a faixa muda o caso (concluir, verificar): a faixa relê. */
  releitura?: unknown;
  /** E205.F1: na aba Pendências da mesa, a lista inteira vem aberta. */
  listaAberta?: boolean;
}

function Pendencias({
  pendencias,
  aberta,
}: {
  pendencias: readonly PendenciaDaProntidaoData[];
  aberta: boolean;
}): JSX.Element {
  return (
    <details
      className="w-full text-body-sm"
      open={aberta || undefined}
      data-testid="proximo-passo-pendencias"
    >
      <summary className="cursor-pointer text-accent-700">
        {t.prontidao.pendencias.replace('{n}', String(pendencias.length))}
      </summary>
      <ol className="mt-sc-2 list-decimal space-y-sc-1 pl-sc-6 text-fg">
        {pendencias.map((p, i) => (
          <li key={`${p.motivo}-${p.peca_id ?? i}`}>
            {t.proximoPasso[p.passo]} — <span className="text-fg-muted">{porque(p)}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

function Indisponivel(): JSX.Element {
  return (
    <p className="mt-sc-4 text-body-sm text-fg-muted" data-testid="proximo-passo-indisponivel">
      {t.prontidao.indisponivel}
    </p>
  );
}

function Faixa({
  caseId,
  prontidao,
  listaAberta,
}: {
  caseId: string;
  prontidao: ProntidaoDoCasoData;
  listaAberta: boolean;
}) {
  const { pathname } = useLocation();
  const passo = prontidao.proximo_passo;
  const tela = telaDoPasso(passo);
  const destino = caminhoDaTela(tela, caseId);
  // E205.F3: as informativas vêm no fim e não são o próximo passo.
  const primeira = prontidao.pendencias.find(bloqueia);
  return (
    <aside
      className="mt-sc-4 flex flex-wrap items-center justify-between gap-sc-3 border border-accent-200 bg-accent-50 px-sc-4 py-sc-3"
      data-testid="proximo-passo"
      data-passo={passo}
      aria-label={t.prontidao.titulo}
    >
      <div>
        <p className="text-caption text-fg-muted">{t.prontidao.titulo}</p>
        <p className="text-body-md font-medium text-fg" data-testid="proximo-passo-acao">
          {t.proximoPasso[passo]}
        </p>
        {primeira ? (
          <p className="text-body-sm text-fg-muted" data-testid="proximo-passo-motivo">
            {porque(primeira)}
          </p>
        ) : null}
      </div>
      {destino === pathname ? (
        <p className="text-body-sm text-fg-muted" data-testid="proximo-passo-aqui">
          {t.prontidao.nestaTela}
        </p>
      ) : (
        <Link
          to={destino}
          className="text-body-sm font-medium text-accent-700 underline"
          data-testid="proximo-passo-ir"
        >
          {t.prontidao.abrir[tela]}
        </Link>
      )}
      {prontidao.pendencias.length > 1 ? (
        <Pendencias pendencias={prontidao.pendencias} aberta={listaAberta} />
      ) : null}
    </aside>
  );
}

/** A faixa sobre a prontidão que a tela já leu (a página do resultado); `null` = não lida agora. */
export function FaixaDoProximoPasso({
  caseId,
  prontidao,
  listaAberta = false,
}: {
  caseId: string;
  prontidao: ProntidaoDoCasoData | null;
  listaAberta?: boolean;
}): JSX.Element {
  return prontidao === null ? (
    <Indisponivel />
  ) : (
    <Faixa caseId={caseId} prontidao={prontidao} listaAberta={listaAberta} />
  );
}

/**
 * A faixa sobre o resultado que a tela já lê para outra decisão (F-E162.F2-02: o laudo eletrônico
 * trava os botões de baixar pela mesma leitura) — uma leitura só por tela.
 */
export function ProximoPassoDoResultado({
  caseId,
  resultado,
  listaAberta = false,
}: {
  caseId: string;
  resultado: EstadoDoResultadoNaTela;
  listaAberta?: boolean;
}): JSX.Element | null {
  if (resultado.estado === 'carregando') return null;
  if (resultado.estado === 'erro') {
    return resultado.motivo === 'indisponivel' ? <Indisponivel /> : null;
  }
  return (
    <FaixaDoProximoPasso
      caseId={caseId}
      prontidao={resultado.dados.prontidao}
      listaAberta={listaAberta}
    />
  );
}

export function ProximoPasso({
  client,
  caseId,
  releitura,
  listaAberta = false,
}: ProximoPassoProps): JSX.Element {
  const { resultado } = useResultadoDoCaso(client, caseId, releitura);
  return (
    <ProximoPassoDoResultado caseId={caseId} resultado={resultado} listaAberta={listaAberta} />
  );
}
